import type { Ring, Vec2 } from '../../../../geo/types';
import { insetOf, ringCentre } from './nieuweKerkChevet';

/**
 * The radiating chapels of 1538 as the floor plan draws them (Reliwiki plattegrond, registered to
 * the trace at 13.6 px/m; docs/plans/landmarks.md 5.5), from the south: the Meeuwisz chapel, the
 * Laurens chapel on the axis (founded 1443), the Boelens chapel (building in 1510) and the Drapeniers
 * chapel (about 1450; De Gids 1942, p. 30). Each stands out from its side of the ambulatory between
 * walls it shares with its neighbours, which meet at the buttresses. The corners of its outer walls
 * are the plan's wall centres, as u and the offset from the axis; the Meeuwisz and Boelens chapels,
 * mirror images on the plan to within 0.3 m, take their mean.
 */
const SIDE_CHAPEL: readonly Vec2[] = [
  [37.5, -12.87],
  [41.8, -11.75],
  [43.2, -8.8],
  [42.68, -4.72],
];

const mirrored = (points: readonly Vec2[]): Vec2[] => [...points].reverse().map(([u, v]): Vec2 => [u, -v]);

export const RADIATING_CHAPELS = [
  { facet: 1, walls: SIDE_CHAPEL },
  /* Closed at the east between the thick ends of its side walls (wall centres at u 45.45), where a
     door opens today in a recess the trace also draws (vertices 76-77, u 44.95): its wall stands in the
     recess, in line with the trace. */
  {
    facet: 2,
    walls: [
      [42.68, -4.72],
      [45.0, -1.72],
      [45.0, 1.72],
      [42.68, 4.72],
    ],
  },
  { facet: 3, walls: mirrored(SIDE_CHAPEL) },
  /* Its north wall runs back to the ambulatory beside the Lady chapel. */
  {
    facet: 4,
    walls: [
      [37.5, 12.87],
      [36.18, 16.74],
      [33.4, 19.02],
      [29.12, 18.8],
    ],
  },
] as const satisfies readonly { facet: number; walls: readonly Vec2[] }[];

/** The chapels' roofs, pitched as the chapels beside the choir. */
export const CHAPEL_PITCH = (55 * Math.PI) / 180;

export interface RadiatingChapel {
  /** Which side of the ambulatory it stands on, counted from the south. */
  readonly facet: number;
  /** Counter-clockwise: the side's south end, the corners of its outer walls, the side's north end. */
  readonly ring: Ring;
  readonly eaveY: number;
  /** The point of its roof, over the middle of its corners. */
  readonly apex: Vec2;
  readonly apexY: number;
}

/** The chapels on the ambulatory's outer corners `outer`, about the axis `vc`, their eaves at `eaveY`. */
export function radiatingChapels(outer: readonly Vec2[], vc: number, eaveY: number): RadiatingChapel[] {
  return RADIATING_CHAPELS.map(({ facet, walls }): RadiatingChapel => {
    const ring: Ring = [outer[1 + facet]!, ...walls.map(([u, v]): Vec2 => [u, vc + v]), outer[2 + facet]!];
    const apex = ringCentre(ring);
    const apexY = eaveY + insetOf(ring, apex) * Math.tan(CHAPEL_PITCH);
    return { facet, ring, eaveY, apex, apexY };
  });
}
